import { MessageCircle, Clock, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { usePublicPlans } from '../hooks/usePublicPlans';

const WHATSAPP_NUMBER = '447934514016';

const formatPrice = (price: string, currency: string) => {
  const num = parseFloat(price);
  if (currency === 'BRL') {
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  }
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'BRL': return 'R$';
    case 'GBP': return '£';
    case 'USD': return '$';
    default: return currency;
  }
};

export default function SubscribePage() {
  const { isPortuguese } = useTranslation();
  const country = isPortuguese ? 'BR' : 'UK';
  const { data: plans, isLoading } = usePublicPlans(country as 'BR' | 'UK');

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    isPortuguese ? 'Oi! Quero assinar o KeepNota' : 'Hi! I want to subscribe to KeepNota'
  )}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Nav */}
      <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Trial Ended Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-6">
            <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {isPortuguese ? 'Seu trial terminou' : 'Your trial has ended'}
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {isPortuguese
              ? 'Continue organizando suas notas fiscais escolhendo um plano abaixo. Seus dados estao salvos e prontos para usar!'
              : 'Keep organizing your receipts by choosing a plan below. Your data is saved and ready to use!'}
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {isLoading ? (
            <div className="col-span-2 text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            plans?.map((plan) => {
              const isPopular = plan.isPopular || plan.type === 'pro';
              const currencySymbol = getCurrencySymbol(plan.currency);

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-8 shadow-lg ${
                    isPopular
                      ? 'bg-green-500 text-white'
                      : 'bg-white dark:bg-gray-800'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-1 rounded-full">
                      {isPortuguese ? 'Mais Popular' : 'Most Popular'}
                    </div>
                  )}

                  <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                    {plan.name}
                  </h3>

                  <div className="flex items-baseline gap-1 mb-6">
                    <span className={`text-4xl font-bold ${isPopular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                      {currencySymbol}{formatPrice(plan.priceMonthly, plan.currency)}
                    </span>
                    <span className={isPopular ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}>
                      {isPortuguese ? '/mes' : '/month'}
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li
                        key={i}
                        className={`flex items-center gap-2 ${
                          isPopular ? 'text-white' : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <Check className={`w-5 h-5 ${isPopular ? 'text-white' : 'text-green-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={`${whatsappLink} - Plano ${plan.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full text-center py-3 rounded-full font-semibold transition ${
                      isPopular
                        ? 'bg-white text-green-600 hover:bg-green-50'
                        : 'border-2 border-green-500 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" />
                      {isPortuguese ? 'Assinar Agora' : 'Subscribe Now'}
                    </span>
                  </a>
                </div>
              );
            })
          )}
        </div>

        {/* Help Section */}
        <div className="text-center bg-gray-100 dark:bg-gray-800 rounded-2xl p-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {isPortuguese ? 'Precisa de ajuda?' : 'Need help?'}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {isPortuguese
              ? 'Fale com a gente pelo WhatsApp. Estamos aqui para ajudar!'
              : 'Chat with us on WhatsApp. We are here to help!'}
          </p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold hover:underline"
          >
            <MessageCircle className="w-5 h-5" />
            {isPortuguese ? 'Falar no WhatsApp' : 'Chat on WhatsApp'}
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        <p>© 2025 KeepNota by Fig & Rosa Ltd.</p>
        <div className="mt-2 space-x-4">
          <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-200">
            {isPortuguese ? 'Termos' : 'Terms'}
          </Link>
          <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-200">
            {isPortuguese ? 'Privacidade' : 'Privacy'}
          </Link>
        </div>
      </footer>
    </div>
  );
}
